import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  TableContainer, Divider, Avatar, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EditIcon from '@mui/icons-material/Edit';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, Cell } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import AddIcon from '@mui/icons-material/Add';
import supabase from '../../../supabaseClient';
import { applyRoundingRules, findMatchingRule } from '../../../Utils/roundingRules';
import AttendanceAddNewRecord from './AttendanceAddNewRecord';
import AddPaymentModal from './AddPaymentModal';

const pad = n => String(n).padStart(2, '0');
const localDateStr = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const fmtTime = d => d ? `${pad(new Date(d).getHours())}:${pad(new Date(d).getMinutes())}` : '—';
const fmtDate = s => { if (!s) return '—'; const d = new Date(s); return `${d.getDate()}.${pad(d.getMonth()+1)}`; };
const fmtMinutes = m => `${Math.floor(m/60)}h ${Math.round(m%60)}m`;

function PeriodPicker({ dateFrom, dateTo, onFromChange, onToChange, periodMode, onPeriodModeChange }) {
  const today = new Date();

  const parseLocal = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

  const setWeek = () => {
    const day = today.getDay() || 7;
    const mon = new Date(today.getFullYear(), today.getMonth(), today.getDate() - day + 1);
    const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
    onFromChange(localDateStr(mon)); onToChange(localDateStr(sun));
    onPeriodModeChange('week');
  };
  const setMonth = () => {
    onFromChange(localDateStr(new Date(today.getFullYear(), today.getMonth(), 1)));
    onToChange(localDateStr(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
    onPeriodModeChange('month');
  };

  const shift = (dir) => {
    const from = parseLocal(dateFrom);
    const to   = parseLocal(dateTo);
    if (periodMode === 'month') {
      const newFrom = new Date(from.getFullYear(), from.getMonth() + dir, 1);
      const newTo   = new Date(newFrom.getFullYear(), newFrom.getMonth() + 1, 0);
      onFromChange(localDateStr(newFrom)); onToChange(localDateStr(newTo));
    } else if (periodMode === 'week') {
      const days = 7 * dir;
      onFromChange(localDateStr(new Date(from.getFullYear(), from.getMonth(), from.getDate() + days)));
      onToChange(localDateStr(new Date(to.getFullYear(),   to.getMonth(),   to.getDate()   + days)));
    } else {
      const diff = Math.round((to - from) / 86400000) + 1;
      onFromChange(localDateStr(new Date(from.getFullYear(), from.getMonth(), from.getDate() + diff * dir)));
      onToChange(localDateStr(new Date(to.getFullYear(),   to.getMonth(),   to.getDate()   + diff * dir)));
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <Button size="small" variant="outlined" onClick={setWeek} sx={{ borderRadius: 2 }}>Ten tydzień</Button>
      <Button size="small" variant="outlined" onClick={setMonth} sx={{ borderRadius: 2 }}>Ten miesiąc</Button>
      <IconButton size="small" onClick={() => shift(-1)} sx={{ border: '1px solid #ccc', borderRadius: 2 }}>
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <input type="date" value={dateFrom} onChange={e => { onFromChange(e.target.value); onPeriodModeChange('custom'); }}
        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc', fontSize: 14 }} />
      <input type="date" value={dateTo} onChange={e => { onToChange(e.target.value); onPeriodModeChange('custom'); }}
        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc', fontSize: 14 }} />
      <IconButton size="small" onClick={() => shift(1)} sx={{ border: '1px solid #ccc', borderRadius: 2 }}>
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function RotatedTick({ x, y, payload }) {
  return (
    <text x={x} y={y} dy={10} textAnchor="end" transform={`rotate(-45 ${x} ${y})`} fontSize={10} fill="#666">
      {payload.value}
    </text>
  );
}

function SummaryCard({ label, value, sub, icon, color = 'primary' }) {
  const colorMap = { primary: '#e3f2fd', success: '#e8f5e9', warning: '#fff8e1', error: '#fce4ec' };
  const textColorMap = { primary: '#1565c0', success: '#2e7d32', warning: '#e65100', error: '#c62828' };
  return (
    <Box sx={{
      flex: 1, minWidth: 150,
      p: 2.5, borderRadius: 3,
      bgcolor: colorMap[color],
      display: 'flex', flexDirection: 'column', gap: 0.5,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: textColorMap[color] }}>
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 800, color: textColorMap[color], lineHeight: 1.2 }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>}
    </Box>
  );
}

function WorkerReportDetail({ employee, idConfig, onBack }) {
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(localDateStr(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [dateTo,   setDateTo]   = useState(localDateStr(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
  const [records,       setRecords]       = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [allPayments,   setAllPayments]   = useState([]);
  const [rules,         setRules]         = useState([]);
  const [periodMode,    setPeriodMode]    = useState('month');
  const [loading,       setLoading]       = useState(false);
  const [showAll,       setShowAll]       = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editSaving,    setEditSaving]    = useState(false);
  const [addAttendanceOpen, setAddAttendanceOpen] = useState(false);
  const [addPaymentOpen,    setAddPaymentOpen]    = useState(false);

  useEffect(() => {
    if (!idConfig) return;
    supabase.from('rounding_rules').select('*')
      .eq('id_configuration', idConfig).eq('is_active', true)
      .then(({ data }) => setRules(data || []));
  }, [idConfig]);

  const calcMinutes = useCallback((r) => {
    if (!r.check_in || !r.check_out) return null;
    const rule = findMatchingRule(r.check_in, rules);
    if (rule) {
      const { billableMinutes } = applyRoundingRules(r.check_in, r.check_out, rule);
      return billableMinutes;
    }
    return Math.max(0, (new Date(r.check_out) - new Date(r.check_in)) / 60000);
  }, [rules]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: att }, { data: pay }, { data: attAll }, { data: payAll }] = await Promise.all([
      supabase.from('attendance')
        .select('id, check_in, check_out, created_at')
        .eq('employee_id', employee.id)
        .gte('check_in', `${dateFrom}T00:00:00`)
        .lte('check_in', `${dateTo}T23:59:59`)
        .order('check_in', { ascending: false }),
      supabase.from('payments')
        .select('id, date, amount, note')
        .eq('employee_id', employee.id)
        .order('date', { ascending: false }),
      supabase.from('attendance')
        .select('check_in, check_out')
        .eq('employee_id', employee.id)
        .not('check_in', 'is', null)
        .not('check_out', 'is', null),
      supabase.from('payments')
        .select('amount')
        .eq('employee_id', employee.id),
    ]);
    setRecords(att || []);
    setPayments(pay || []);
    setAllAttendance(attAll || []);
    setAllPayments(payAll || []);
    setLoading(false);
  }, [employee.id, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Computed values ──────────────────────────────────────────────
  const completedRecords = records.filter(r => r.check_in && r.check_out);

  const totalMinutes = completedRecords.reduce((acc, r) => {
    return acc + (calcMinutes(r) ?? 0);
  }, 0);

  const hourlyRate = employee.hourly_rate || 0;
  const grossSalary = (totalMinutes / 60) * hourlyRate;

  // Period: payments within selected date range
  const periodPaid = payments.filter(p => p.date >= dateFrom && p.date <= dateTo)
    .reduce((a, p) => a + p.amount, 0);
  const periodRemaining = Math.max(0, grossSalary - periodPaid);

  // Lifetime: all-time salary minus all-time payments (clamped to 0)
  const lifetimeMinutes = allAttendance.reduce((acc, r) => {
    return acc + (calcMinutes(r) ?? 0);
  }, 0);
  const lifetimeSalary = (lifetimeMinutes / 60) * hourlyRate;
  const lifetimePaid = allPayments.reduce((a, p) => a + p.amount, 0);
  const lifetimeRemaining = Math.max(0, lifetimeSalary - lifetimePaid);

  // Bar chart data — every day in the selected period, 0h where there's no record
  const chartData = (() => {
    const parseLocal = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
    const map = {};
    completedRecords.forEach(r => {
      const dayStr = localDateStr(new Date(r.check_in));
      const mins = calcMinutes(r) ?? 0;
      map[dayStr] = (map[dayStr] || 0) + mins;
    });
    const from = parseLocal(dateFrom);
    const to = parseLocal(dateTo);
    const days = [];
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const dayStr = localDateStr(d);
      days.push({
        day: dayStr,
        label: `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`,
        hours: +((map[dayStr] || 0) / 60).toFixed(2),
      });
    }
    return days;
  })();

  const periodLabel = (() => {
    const from = new Date(dateFrom);
    return from.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
  })();

  // ── Export PDF ───────────────────────────────────────────────────
  const pl = s => String(s)
    .replace(/ą/g,'a').replace(/Ą/g,'A')
    .replace(/ć/g,'c').replace(/Ć/g,'C')
    .replace(/ę/g,'e').replace(/Ę/g,'E')
    .replace(/ł/g,'l').replace(/Ł/g,'L')
    .replace(/ń/g,'n').replace(/Ń/g,'N')
    .replace(/ó/g,'o').replace(/Ó/g,'O')
    .replace(/ś/g,'s').replace(/Ś/g,'S')
    .replace(/ź/g,'z').replace(/Ź/g,'Z')
    .replace(/ż/g,'z').replace(/Ż/g,'Z')
    .replace(/zł/g,'zl').replace(/–/g,'-').replace(/—/g,'-');

  const plRow = row => row.map(cell => pl(String(cell ?? '')));

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(pl(`${employee.name} ${employee.surname}`), 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(pl(`${employee.position ?? 'Pracownik'} · ${hourlyRate.toFixed(2)} zl/h`), 14, 25);
    doc.text(`Okres: ${dateFrom} - ${dateTo}`, 14, 31);
    doc.setTextColor(0);

    doc.setFontSize(11);
    doc.text('Podsumowanie', 14, 42);
    autoTable(doc, {
      startY: 46,
      head: [['Laczne godziny', 'Zmian', 'Do wyplaty (okres)', 'Do wyplaty (wszystkie)']],
      body: [[fmtMinutes(totalMinutes), completedRecords.length, `${periodRemaining.toFixed(2)} zl`, `${lifetimeRemaining.toFixed(2)} zl`]],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [25, 118, 210] },
    });

    doc.text('Historia zmian', 14, doc.lastAutoTable.finalY + 10);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 14,
      head: [['Data', 'Wejscie', 'Wyjscie', 'Czas', 'Kwota', 'Status']],
      body: records.map(r => {
        const mins = calcMinutes(r);
        return plRow([
          fmtDate(r.check_in),
          fmtTime(r.check_in),
          fmtTime(r.check_out),
          mins !== null ? fmtMinutes(mins) : '-',
          mins !== null ? `${((mins / 60) * hourlyRate).toFixed(2)} zl` : '-',
          r.check_out ? 'zakonczona' : 'w pracy',
        ]);
      }),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [25, 118, 210] },
    });

    if (payments.length > 0) {
      doc.text('Historia wyplat', 14, doc.lastAutoTable.finalY + 10);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 14,
        head: [['Data', 'Notatka', 'Kwota', 'Miesiac']],
        body: payments.map(p => plRow([
          p.date,
          p.note || '-',
          `${p.amount.toFixed(2)} zl`,
          new Date(p.date).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' }),
        ])),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] },
      });
    }

    doc.save(`raport_${employee.surname}_${employee.name}_${dateFrom}_${dateTo}.pdf`);
  };

  // ── Export Excel: Lista obecności ───────────────────────────────
  const exportExcel = () => {
    const parseLocal = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
    const from = parseLocal(dateFrom);
    const to = parseLocal(dateTo);
    const days = [];
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) days.push(new Date(d));

    const header = [
      [`Lista obecności pracownika — ${employee.name} ${employee.surname}`],
      [`Za okres od ${dateFrom} do ${dateTo}`],
      ['Kierownik jednostki: .............................................'],
      [],
      ['Lp.', 'Data', 'Godzina rozpoczęcia', 'Godzina zakończenia', 'Suma godzin', 'Podpis kierownika'],
    ];
    const HEADER_BANNER_ROWS = 3;

    const absentRowIdx = [];
    const rows = days.map((day, i) => {
      const dayStr = localDateStr(day);
      const dayRecords = records.filter(r => r.check_in && localDateStr(new Date(r.check_in)) === dayStr);

      if (dayRecords.length === 0) {
        absentRowIdx.push(header.length + i);
        return [i + 1, dayStr, 'NIEOBECNY', '', '', ''];
      }

      const starts = dayRecords.map(r => new Date(r.check_in));
      const completed = dayRecords.filter(r => r.check_out);
      const start = new Date(Math.min(...starts));
      const dayMinutes = completed.reduce((acc, r) => acc + (calcMinutes(r) ?? 0), 0);

      if (completed.length === 0) {
        return [i + 1, dayStr, fmtTime(start), 'w trakcie', '', ''];
      }

      const ends = completed.map(r => new Date(r.check_out));
      const end = new Date(Math.max(...ends));
      return [i + 1, dayStr, fmtTime(start), fmtTime(end), +(dayMinutes / 60).toFixed(2), ''];
    });

    const totalRow = ['', '', '', 'Σ', +(totalMinutes / 60).toFixed(2), ''];

    const aoa = [...header, ...rows, totalRow];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 5 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 22 }];
    ws['!merges'] = [
      ...Array.from({ length: HEADER_BANNER_ROWS }, (_, r) => ({ s: { r, c: 0 }, e: { r, c: 5 } })),
      ...absentRowIdx.map(r => ({ s: { r, c: 2 }, e: { r, c: 4 } })),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lista obecności');
    XLSX.writeFile(wb, `lista_obecnosci_${employee.surname}_${employee.name}_${dateFrom}_${dateTo}.xlsx`);
  };

  const toDatetimeLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEdit = (r) => setEditingRecord({ id: r.id, check_in: toDatetimeLocal(r.check_in), check_out: toDatetimeLocal(r.check_out) });

  const handleSaveEdit = async () => {
    setEditSaving(true);
    await supabase.from('attendance').update({
      check_in:  editingRecord.check_in  ? new Date(editingRecord.check_in).toISOString()  : null,
      check_out: editingRecord.check_out ? new Date(editingRecord.check_out).toISOString() : null,
    }).eq('id', editingRecord.id);
    setEditSaving(false);
    setEditingRecord(null);
    fetchData();
  };

  const displayedRecords = showAll ? records : records.slice(0, 4);

  const thCell = { fontWeight: 600, color: 'text.secondary', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid', borderColor: 'divider', py: 1.5 };

  return (
    <Box>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 0.5 }}>
        <IconButton onClick={onBack} size="small" sx={{ color: 'primary.main' }}><ArrowBackIcon fontSize="small" /></IconButton>
        <Typography variant="body2" sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }} onClick={onBack}>
          Wszyscy pracownicy
        </Typography>
      </Box>

      {/* Worker header card */}
      <Paper elevation={0} sx={{ p: 3, mb: 2, borderRadius: 3, background: 'linear-gradient(135deg, #A84355 0%, #1976d2 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '1.2rem', fontWeight: 700, color: '#fff', border: '2px solid rgba(255,255,255,0.4)' }}>
              {employee.name?.[0]}{employee.surname?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                {employee.name} {employee.surname}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.3 }}>
                {employee.position ?? 'Pracownik'} · {hourlyRate.toFixed(2)} zł/h
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={exportExcel}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none', borderRadius: 2 }}>
              Excel
            </Button>
            <Button variant="contained" size="small" startIcon={<PictureAsPdfIcon />} onClick={exportPDF}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, color: '#fff', boxShadow: 'none', borderRadius: 2 }}>
              PDF
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Period picker — below header, always visible */}
      <Box sx={{ mb: 3 }}>
        <PeriodPicker dateFrom={dateFrom} dateTo={dateTo} onFromChange={setDateFrom} onToChange={setDateTo} periodMode={periodMode} onPeriodModeChange={setPeriodMode} />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <SummaryCard label="Łączne godziny" value={fmtMinutes(totalMinutes)}
              sub={periodLabel} icon={<AccessTimeIcon sx={{ fontSize: 16 }} />} color="primary" />
            <SummaryCard label="Zmian" value={completedRecords.length}
              sub="ukończonych" icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />} color="primary" />
            <SummaryCard label="Zarobił" value={`${grossSalary.toFixed(2)} zł`}
              sub={`${(totalMinutes/60).toFixed(2)}h × ${hourlyRate} zł`}
              icon={<AttachMoneyIcon sx={{ fontSize: 16 }} />} color="warning" />
            <SummaryCard label="Do wypłaty za dany okres" value={`${periodRemaining.toFixed(2)} zł`}
              sub={periodRemaining === 0 ? 'rozliczone' : 'po odliczeniu zaliczek'}
              icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />} color={periodRemaining > 0 ? 'success' : 'primary'} />
            <SummaryCard label="Suma do wypłaty (wszystkie okresy)" value={`${lifetimeRemaining.toFixed(2)} zł`}
              sub={lifetimeRemaining === 0 ? 'rozliczone' : 'łącznie do wypłaty'}
              icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />} color={lifetimeRemaining > 0 ? 'success' : 'primary'} />
          </Box>

          {/* Bar chart */}
          {chartData.length > 0 && (
            <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Godziny pracy</Typography>
                <Chip label={periodLabel} size="small" variant="outlined" />
              </Box>
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: -16, bottom: 24 }}>
                  <XAxis dataKey="label" tick={<RotatedTick />} interval={0} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}h`} axisLine={false} tickLine={false} />
                  <ChartTooltip
                    formatter={(v) => [`${v}h`, 'Godziny']}
                    labelFormatter={l => `Dzień ${l}`}
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}
                  />
                  <Bar dataKey="hours" radius={[6,6,0,0]}>
                    {chartData.map((_, i) => <Cell key={i} fill="#1976d2" opacity={0.85} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Shifts table */}
          <Paper elevation={0} variant="outlined" sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Historia zmian</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={`${records.length} wpisów`} size="small" variant="outlined" />
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddAttendanceOpen(true)} sx={{ borderRadius: 2 }}>
                  Dodaj wpis
                </Button>
              </Stack>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={thCell}>Data</TableCell>
                    <TableCell sx={thCell}>Wejście</TableCell>
                    <TableCell sx={thCell}>Wyjście</TableCell>
                    <TableCell sx={thCell}>Czas</TableCell>
                    <TableCell sx={thCell}>Kwota</TableCell>
                    <TableCell sx={thCell}>Status</TableCell>
                    <TableCell sx={thCell} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedRecords.map((r, idx) => {
                    const mins = calcMinutes(r);
                    const done = !!r.check_out;
                    return (
                      <TableRow key={r.id} sx={{ '&:hover': { bgcolor: 'action.hover' }, bgcolor: idx % 2 === 0 ? 'transparent' : 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{fmtDate(r.check_in)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.88rem' }}>{fmtTime(r.check_in)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.88rem' }}>{done ? fmtTime(r.check_out) : '—'}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{mins !== null ? fmtMinutes(mins) : '—'}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'success.dark' }}>
                          {mins !== null ? `${((mins/60)*hourlyRate).toFixed(2)} zł` : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={done ? 'zakończona' : 'w trakcie'}
                            size="small"
                            color={done ? 'default' : 'primary'}
                            variant={done ? 'outlined' : 'filled'}
                            sx={{ fontSize: '0.7rem', fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 1 }}>
                          <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {records.length > 4 && (
              <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Typography variant="body2"
                  sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setShowAll(s => !s)}>
                  {showAll ? '↑ Pokaż mniej' : `↓ Pokaż wszystkie (${records.length - 4} więcej)`}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Payments table */}
          <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Historia wypłat</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={`${payments.length} wypłat`} size="small" variant="outlined" color="success" />
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddPaymentOpen(true)} sx={{ borderRadius: 2 }}>
                  Dodaj wypłatę
                </Button>
              </Stack>
            </Box>
            {payments.length === 0 ? (
              <Box sx={{ px: 3, py: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Brak wypłat</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={thCell}>Data</TableCell>
                      <TableCell sx={thCell}>Notatka</TableCell>
                      <TableCell sx={thCell}>Kwota</TableCell>
                      <TableCell sx={thCell}>Miesiąc</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p, idx) => (
                      <TableRow key={p.id} sx={{ '&:hover': { bgcolor: 'action.hover' }, bgcolor: idx % 2 === 0 ? 'transparent' : 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>{p.date}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontStyle: p.note ? 'normal' : 'italic' }}>{p.note || 'brak notatki'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: 'success.light', color: 'success.dark', px: 1.5, py: 0.3, borderRadius: 2, fontWeight: 700, fontSize: '0.85rem' }}>
                            {p.amount.toFixed(2)} zł
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                          {new Date(p.date).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Edit attendance dialog */}
      <Dialog open={!!editingRecord} onClose={() => setEditingRecord(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edytuj wpis</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField
            label="Wejście"
            type="datetime-local"
            value={editingRecord?.check_in || ''}
            onChange={e => setEditingRecord(r => ({ ...r, check_in: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
          <TextField
            label="Wyjście"
            type="datetime-local"
            value={editingRecord?.check_out || ''}
            onChange={e => setEditingRecord(r => ({ ...r, check_out: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setEditingRecord(null)} variant="outlined" size="small" sx={{ borderRadius: 2 }}>
            Anuluj
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" size="small" disabled={editSaving} sx={{ borderRadius: 2 }}>
            {editSaving ? 'Zapisywanie…' : 'Zapisz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add attendance record */}
      <AttendanceAddNewRecord
        open={addAttendanceOpen}
        onClose={() => setAddAttendanceOpen(false)}
        onRefresh={fetchData}
        idConfig={idConfig}
        lockedEmployeeId={employee.id}
        lockedEmployeeName={`${employee.name} ${employee.surname}`}
      />

      {/* Add payment */}
      <AddPaymentModal
        open={addPaymentOpen}
        onClose={() => setAddPaymentOpen(false)}
        employee={employee}
        idConfig={idConfig}
        onRefresh={fetchData}
      />
    </Box>
  );
}

export default WorkerReportDetail;
